import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Image from 'react-bootstrap/Image';

type Props = {
  playlist: {
    id: string;
    level: number;
    progressToNextLevel: number;
    totalXP?: number;
    currentMonthXP?: number;
    accuracy?: number;
    highestScore?: number;
    averageScore?: number;
    roundPlayedCount?: number;
    contentDiscoveredCount?: number;
    totalContentItemsCount?: number;
    contentPack: { id: string; name: string; picture?: string | null };
  };
  typeLabel?: string;
  canDelete?: boolean;
  onChange?: (newPlaylist: {
    id: string;
    level: number;
    progressToNextLevel: number;
    totalXP?: number;
    currentMonthXP?: number;
    accuracy?: number;
    highestScore?: number;
    averageScore?: number;
    roundPlayedCount?: number;
    contentDiscoveredCount?: number;
    totalContentItemsCount?: number;
    contentPack: { id: string; name: string; picture?: string | null };
  }) => void;
  onSave?: (playlist: Props['playlist']) => void;
  onSetCurrent?: (playlist: Props['playlist']) => void;
  onDelete?: (contentPackId: string) => void;
};

const formatStat = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return Number.isInteger(value) ? `${value}` : value.toFixed(1);
};

export default function PlaylistEditor({
  playlist,
  typeLabel = 'Saved',
  canDelete = false,
  onChange,
  onSave,
  onSetCurrent,
  onDelete,
}: Props) {
  const [imageFailed, setImageFailed] = useState(false);
  const picture = playlist.contentPack.picture?.trim();
  const showPicture = picture && !imageFailed;

  // @ts-ignore
  const handleChangePlaylist = (key: string) => (event) => {
    const { value } = event.target;
    if (onChange) {
      onChange({ ...playlist, [key]: value });
    }
  };

  if (!playlist) return null;

  return (
    <tr>
      <td className="align-middle text-center text-nowrap">
        <span className={typeLabel === 'Current Basic' ? 'badge badge-secondary' : 'badge badge-primary'}>
          {typeLabel}
        </span>
      </td>
      <td className="align-middle">
        <div className="d-flex align-items-center">
          {showPicture ? (
            <Image
              roundedCircle
              className="mr-2"
              src={picture}
              style={{ width: 36, height: 36, objectFit: 'cover' }}
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div
              className="mr-2 rounded-circle bg-light border text-muted d-flex align-items-center justify-content-center"
              style={{ width: 36, height: 36, fontSize: 12, flex: '0 0 36px' }}
              title="No playlist cover"
            >
              {playlist.contentPack.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <div>
            <div>{playlist.contentPack.name || playlist.contentPack.id}</div>
            <div className="small text-muted">{playlist.contentPack.id}</div>
          </div>
        </div>
      </td>
      <td className="align-middle text-center" style={{ minWidth: 80 }}>
        <Form.Control size="sm" value={playlist.level} onChange={handleChangePlaylist('level')} />
      </td>
      <td className="align-middle text-center" style={{ minWidth: 130 }}>
        <Form.Control
          size="sm"
          value={playlist.progressToNextLevel}
          onChange={handleChangePlaylist('progressToNextLevel')}
        />
      </td>
      <td className="align-middle text-center" style={{ minWidth: 110 }}>
        <Form.Control
          size="sm"
          value={playlist.totalXP ?? ''}
          onChange={handleChangePlaylist('totalXP')}
          placeholder="-"
        />
      </td>
      <td className="align-middle text-center" style={{ minWidth: 110 }}>
        <Form.Control
          size="sm"
          value={playlist.currentMonthXP ?? ''}
          onChange={handleChangePlaylist('currentMonthXP')}
          placeholder="-"
        />
      </td>
      <td className="align-middle text-center" style={{ minWidth: 90 }}>
        <Form.Control
          size="sm"
          value={playlist.roundPlayedCount ?? ''}
          onChange={handleChangePlaylist('roundPlayedCount')}
          placeholder="-"
        />
      </td>
      <td className="align-middle text-center" style={{ minWidth: 90 }}>
        <Form.Control
          size="sm"
          value={playlist.accuracy ?? ''}
          onChange={handleChangePlaylist('accuracy')}
          placeholder="-"
        />
      </td>
      <td className="align-middle text-center" style={{ minWidth: 90 }}>
        <Form.Control
          size="sm"
          value={playlist.averageScore ?? ''}
          onChange={handleChangePlaylist('averageScore')}
          placeholder="-"
        />
      </td>
      <td className="align-middle text-center" style={{ minWidth: 90 }}>
        <Form.Control
          size="sm"
          value={playlist.highestScore ?? ''}
          onChange={handleChangePlaylist('highestScore')}
          placeholder="-"
        />
      </td>
      <td className="align-middle text-center">
        {formatStat(playlist.contentDiscoveredCount)} / {formatStat(playlist.totalContentItemsCount)}
      </td>
      <td className="align-middle text-center">
        {onSave && (
          <Button variant="primary" size="sm" className="mr-1" onClick={() => onSave(playlist)}>
            Save
          </Button>
        )}
        {onSetCurrent && (
          <Button variant="outline-primary" size="sm" className="mr-1" onClick={() => onSetCurrent(playlist)}>
            Set Current
          </Button>
        )}
        {canDelete && onDelete && (
          <Button variant="danger" size="sm" onClick={() => onDelete(playlist.contentPack.id)}>
            X
          </Button>
        )}
      </td>
    </tr>
  );
}
